import { range, timeout } from 'widgets/toolbox/util';
import { submitFormJson, getContentByUrl } from 'widgets/toolbox/ajax';
import { scrollWindowTo } from 'widgets/toolbox/scroll';

/**
 * @typedef {typeof import('widgets/Widget').default} Widget
 * @typedef {InstanceType<ReturnType<typeof import('widgets/forms/InputSelect').default>>} inputSelect
 * @typedef {InstanceType<typeof import('widgets/toolbox/RefElement').RefElement>} refElement
 * @typedef {InstanceType<ReturnType<typeof import('widgets/global/ConfirmDialog').default>>} confirmDialog
 * @typedef {InstanceType<ReturnType<typeof import('widgets/global/Modal').default>>} modal
 * @typedef {{items: Array<object>, shipments: Array<object>, valid: object, error: string, redirectUrl: string, couponAction: string}|undefined} Cart
 */

/**
 * @description Base CartMgr implementation
 * @param {Widget} Widget Base widget for extending
 * @returns {typeof CartMgr} Cart Manager class
 */
export default function (Widget) {
    /**
     * @category widgets
     * @subcategory cart
     * @class CartMgr
     * @augments Widget
     * @classdesc CartMgr component on Cart page. Renders the whole cart template on each update e.g. quantity/coupon/product edit etc.
     * Widget manages next actions:
     * 1. Edit product in a cart
     * 2. Change product quantity
     * 3. Remove product (move to Wish list) from Cart
     * 4. Communicate actions results
     * 5. Re-render Cart including products list and cart summary in case of actions like: product updated, coupon applied etc.
     * 6. Enable/disable "Secure Checkout" button to move to Checkout process depending on if there are errors in cart, which prevents moving to Checkout
     * @property {string} data-widget - Widget name `cartMgr`
     * @property {Array} data-items - Cart items list
     * @property {Array} data-approaching-discounts - Cart approaching discounts list
     * @property {object} data-action-urls - Cart action URLs
     * @property {object} data-totals - Cart totals
     * @property {Array} data-shipments - Cart shipments list
     * @property {boolean} data-valid - Is Cart valid
     * @property {number} data-num-items - Number of items in Cart
     * @property {string} data-cart-get-url - An URL to get updated Cart object from server
     * @property {object} data-accessibility-alerts - Accessibility alerts messages for different user actions
     * Possible values are: `quantitychanged`, `productupdated`, `productremoved`,
     * `promocodeadded`, `promocodeapplied`, `promocoderemoved`, `addedtowishlist`
     * @example <caption>Example of CartMgr widget markup</caption>
     * <div
     *     data-widget="cartMgr"
     *     data-items="${JSON.stringify(pdict.items.toArray())}"
     *     data-approaching-discounts="${JSON.stringify(pdict.approachingDiscounts.toArray())}"
     *     data-action-urls="${JSON.stringify(pdict.actionUrls)}"
     *     data-cart-get-url="${URLUtils.url('Cart-Get')}"
     *     data-totals="${JSON.stringify(pdict.totals)}"
     *     data-shipments="${JSON.stringify(pdict.shipments.toArray())}"
     *     data-valid="${JSON.stringify(pdict.valid)}"
     *     data-num-items="${JSON.stringify(pdict.numItems)}"
     *     data-accessibility-alerts='{
     *         "quantitychanged": "${Resource.msg('alert.quantitychanged', 'cart', null)}",
     *         ...
     *     }'
     * >
     *     <div data-ref="cartContainer"></div>
     *     <script data-ref="cartTemplate" type="template/mustache">
     *         <div data-ref="cartContainer">
     *              ... cart global errors
     *              ... cart approaching discounts
     *              ... cart items
     *              ... cart summary
     *         </div>
     *     </script>
     * </div>
     */
    class CartMgr extends Widget {
        prefs() {
            return {
                cartGetUrl: '',
                accessibilityAlerts: {},
                actionUrls: {},
                tauAttrRemoveProductConfirmationPopUp: 'remove_item_confirmation_dialog',
                items: [],
                shipments: [],
                valid: true,
                error: '',
                redirectUrl: '',
                couponAction: '',
                ...super.prefs()
            };
        }

        /**
         * @description Widget initialisation
         * @listens "minicart.updated"
         * @listens "product.updated"
         * @listens "coupon.updated"
         * @emits "cart.rendered"
         * @returns {void}
         */
        init() {
            this.cart = this.prefs();
            // render in next tick so will not block initialization
            this.onDestroy(timeout(() => {
                this.renderCart(this.cart).then(() => {
                    this.eventBus().emit('cart.rendered');
                });
            }, 0));

            this.eventBus().on('minicart.updated', 'renderCart');
            this.eventBus().on('product.updated', 'onProductUpdated');
            this.eventBus().on('coupon.updated', 'onCouponUpdated');
        }

        /**
         * @description Executes on product updated in Quick View
         * @param {object} response Response object from server
         * @returns {void}
         */
        onProductUpdated(response) {
            const cart = response.cartModel;

            if (cart) {
                this.renderCart(cart)
                    .then(() => this.accessibilityAlert(this.prefs().accessibilityAlerts.productupdated));
            }
        }

        /**
         * @description Executes on product updated in Quick View
         * @param {Cart} cart Cart object
         * @returns {void}
         */
        onCouponUpdated(cart) {
            this.renderCart(cart)
                .then(() => {
                    if (cart) {
                        const accessibilityAlert = this.prefs().accessibilityAlerts[`promocode${cart.couponAction}`];
                        this.accessibilityAlert(accessibilityAlert);
                    }
                });
        }

        /**
         * @description Renders Cart object and saves it as a current cart state
         * Adds some information into Cart object proior to rendering,
         * to make the whole object mustache template friendly
         * @emits "cart.updated"
         * @param {Cart} cart - Cart object to reflect in template
         * @returns {Promise} result
         */
        renderCart(cart) {
            if (!cart) {
                return Promise.reject(new Error());
            }

            cart.items = cart.items.map(item => {
                const itemObject = { ...item };

                if (!item.quantityOptions) {
                    return itemObject;
                }

                itemObject.quantityOptionsList = range(
                    item.quantityOptions.minOrderQuantity,
                    item.quantityOptions.maxOrderQuantity + 1
                ).map(qty => ({
                    index: qty,
                    selected: qty === item.quantity ? 'selected' : ''
                }));

                if (item.quantity > item.quantityOptions.maxOrderQuantity) {
                    itemObject.quantityOptionsList.push({
                        index: item.quantity,
                        selected: 'selected'
                    });
                }

                return itemObject;
            });

            cart.shipments = (cart.shipments || []).map(shipment => {
                shipment.shippingMethods = (shipment.shippingMethods || []).map(shippingMethod => ({
                    ...shippingMethod,
                    selected: shippingMethod.ID === shipment.selectedShippingMethod ? 'selected' : ''
                }));
                if (!shipment.shippingMethods.length) {
                    shipment.noShippingMethodsAvailable = true;
                }
                shipment.selectedShippingMethodObject = (shipment.shippingMethods || [])
                    .find(shippingMethod => shippingMethod.ID === shipment.selectedShippingMethod);
                return shipment;
            });

            this.cart = cart;

            /**
             * @description Event, indicates that cart content was updated
             * @event "cart.updated"
             */
            this.eventBus().emit('cart.updated', cart);

            return this.render('cartTemplate', cart, this.ref('cartContainer'));
        }

        /**
         * @description Updates quantity of choosen product in Cart page
         * @param {inputSelect} inputSelect - Select quantity input
         * @returns {void}
         */
        updateQty(inputSelect) {
            this.showProgressBar();
            const uuid = inputSelect.data('uuid');
            submitFormJson(inputSelect.data('action'), {
                pid: inputSelect.data('pid'),
                uuid: uuid,
                quantity: inputSelect.getValue()
            }, 'GET')
                .then((resp) => {
                    this.renderCartResponse(resp);
                    this.accessibilityAlert(this.prefs().accessibilityAlerts.quantitychanged);
                })
                .catch((error) => {
                    if (error && error.error && error.errorMessage) {
                        this.render('errorTemplate', { message: error.errorMessage }, this.ref('errorMsgs'));
                    } else {
                        this.renderCartWithItemLevelActionError(uuid, error.message);
                    }
                })
                .finally(() => {
                    this.hideProgressBar();
                });
        }

        /**
         * @description Fires `alert.show` event to be handled with Global Alerting System
         * @param {string} accessibilityAlert - alert string
         * @emits "alert.show"
         * @returns {void}
         */
        accessibilityAlert(accessibilityAlert) {
            this.eventBus().emit('alert.show', {
                accessibilityAlert
            });
        }

        /**
         * @description Renders cart response from server.
         * Follows redirect URL in case if cart was not found on server
         * (request to update product/quantity etc)
         * @param {Cart} resp - server response
         * @returns {void}
         */
        renderCartResponse(resp) {
            if (resp && resp.error && resp.redirectUrl) {
                window.location.assign(resp.redirectUrl);
            } else {
                this.renderCart(resp);
            }
        }

        /**
         * @description Handler for "Edit" product button in a Cart
         * @param {refElement} editBtn - Target "Edit" button
         * @emits "product.tile.qv.open.edit"
         * @returns {void}
         */
        editProduct(editBtn) {
            this.showProgressBar();
            const uuid = editBtn.data('uuid');

            getContentByUrl(editBtn.data('href'), { uuid })
                .then((response) => {
                    this.getById('editProductModal', (/** @type {modal} */ editProductModal) => editProductModal
                        .showModal({
                            body: response,
                            attributes: {
                                'data-tau-unique': 'edit_product_dialog'
                            }
                        }));

                    this.eventBus().emit('product.tile.qv.open.edit', editBtn);
                })
                .catch(error => {
                    this.renderCartWithItemLevelActionError(uuid, error.message);
                })
                .finally(() => {
                    this.hideProgressBar();
                });
        }

        /**
         * @description Handler for "Remove" product button in a Cart
         * Stores clicked button instance to property because of "Remove Popup" displaying and further interaction
         * @param {refElement} removeButton - Clicked "Remove" product button
         * @returns {void}
         */
        removeProduct(removeButton) {
            this.removeButton = removeButton;
            this.getById('confirmDialog', (/** @type {confirmDialog} */ confirmDialog) => {
                confirmDialog.showModal({
                    productName: removeButton.data('name'),
                    attributes: {
                        'data-tau': this.prefs().tauAttrRemoveProductConfirmationPopUp
                    }
                });
            });
        }

        /**
         * @description Executes when user clicks on product details link in the cart.
         * Usually used by analytics etc.
         * @emits "cart.product.link.click"
         * @param {refElement} link - clicked product tile link
         * @returns {void}
         */
        onProductLinkClick(link) {
            this.eventBus().emit('cart.product.link.click', link);
        }

        /**
         * @description Renders Cart with error message on Cart Item level.
         * Usually it may happens when in parallel tab user removed product from cart.
         * As far as we should display error on product, which was removed, thus we don't need
         * to update this exact item in a stored earlier cart object
         * @param {string} itemUUID - Product item identifier
         * @param {string} error - an error response from the server
         * @returns {void}
         */
        renderCartWithItemLevelActionError(itemUUID, error) {
            if (!this.cart) {
                return;
            }

            this.cart.items = this.cart.items.map(item => {
                const existingOldItem = this.cart && this.cart.items.find((cartItem) => cartItem.UUID === item.UUID);
                const returnItem = !existingOldItem ? item : existingOldItem;
                return {
                    ...returnItem,
                    actionErrorMessage: (itemUUID === item.UUID) ? error : ''
                };
            });

            this.cart.valid.error = true;

            this.renderCart(this.cart);
        }

        /**
         * @description A callback, which is executed, when customer agreed to remove product in confirmation popup
         * @param {refElement|null} button - Button, pressed in confirmation modal
         * @param {boolean} [movedToWishlist] - Indicates if product was moved to wishlist
         * @emits "cart.remove.product"
         * @returns {void}
         */
        confirmedRemoveProduct(button, movedToWishlist = false) {
            if (this.removeButton) {
                const removeButtonUUID = this.removeButton.data('uuid');
                this.eventBus().emit('cart.remove.product', this);
                this.showProgressBar();
                submitFormJson(this.removeButton.data('removeAction') || this.removeButton.data('action'), {
                    pid: this.removeButton.data('pid'),
                    uuid: removeButtonUUID
                }, 'POST')
                    .then((response) => {
                        this.confirmationRemoveCallback(response.basket, movedToWishlist);
                    })
                    .catch(error => {
                        this.renderCartWithItemLevelActionError(removeButtonUUID, error.message);
                    })
                    .finally(() => {
                        this.hideProgressBar();
                    });
                this.removeButton = undefined;
            }
        }

        /**
         * @description Move product from cart to wishlist.
         * In fact this functionality implemented in 2 steps:
         * 1. Add product to wishlist
         * 2. Remove product from cart
         * This is done due to possible backend code duplication in one universal endpoint
         * @returns {void}
         */
        moveProductToWishlist() {
            if (this.removeButton) {
                const removeButtonUUID = this.removeButton.data('uuid');
                this.showProgressBar();
                submitFormJson(this.prefs().actionUrls.moveProductToWishlist, {
                    pid: this.removeButton.data('pid')
                }).then(() => {
                    this.confirmedRemoveProduct(null, true);
                }).catch(error => {
                    this.renderCartWithItemLevelActionError(removeButtonUUID, error.message);
                }).finally(() => {
                    this.hideProgressBar();
                });
            }
        }

        /**
         * @description A callback, which is executed once `remove` product server call was performed
         * @param {Cart} cart - server response object
         * @param {boolean} [movedToWishlist] - Indicates if product was moved to wishlist
         * @returns {void}
         */
        confirmationRemoveCallback(cart, movedToWishlist = false) {
            this.renderCart(cart)
                .then(() => this.accessibilityAlert(
                    movedToWishlist
                        ? this.prefs().accessibilityAlerts.movedtowishlist
                        : this.prefs().accessibilityAlerts.productremoved
                ));
        }

        /**
         * @description Shows progress bar
         * @returns {void}
         */
        showProgressBar() {
            this.ref('cartContainer').attr('aria-busy', 'true');
        }

        /**
         * @description Hides progress bar
         * @returns {void}
         */
        hideProgressBar() {
            this.ref('cartContainer').attr('aria-busy', 'false');
        }

        /**
         * @description Handles `proceed to checkout` button click.
         * <br>In case of error - to not allow Customer to enter checkout flow and scroll to cart error message
         * @param {refElement} btn - clicked button
         * @param {Event} event - target event
         * @emits "cart.page.submitted"
         * @returns {void}
         */
        proceedToCheckout(btn, event) {
            if (btn.data('error')) {
                event.preventDefault();
                this.scrollToError();
            }

            this.eventBus().emit('cart.page.submitted', this);
        }

        /**
         * @description Scrolls cart error messages into viewport
         * @returns {void}
         */
        scrollToError() {
            const cartErrorMessage = this.ref('errorMsgs').get();
            if (cartErrorMessage) {
                scrollWindowTo(cartErrorMessage);
            }
        }
    }

    return CartMgr;
}
